import { useState } from "react"
import PropTypes from "prop-types"
import { get, map, isFinite, isEmpty, isEqual } from "lodash"
import pluralize from "pluralize"
import { IconButton, Popper, ClickAwayListener } from "@mui/material"
import { Add, History, Save, Edit } from "@mui/icons-material"
import { formatNiceShortDate } from "helpers/formatters/dates"
import { LOADING } from "helpers/data-statuses"
import NewName from "./components/new-name"
import EditName from "./components/edit-name"
import SearchList from "components/search-list"
import OPTooltip from "components/material/tooltip"
import "./styles.scss"

const EMPTY_STATE = {
  menuParent: null,
}

function FilterSaver({
  id,
  account,
  modelId,
  name,
  resultsCount,
  filter,
  filters,
  filtersCount,
  status,
  setFilter,
  modifyFilter,
  currentFilter,
  setCurrentFilter,
  create,
  update,
  del,
  openDialog,
}) {
  const [state, setState] = useState(EMPTY_STATE)

  const saveFilter = (name) => {
    create("filters", `/api/${account}/filters/${modelId}`, {
      filter,
      name,
      count: resultsCount,
      nbFilters: filtersCount,
    }, {
      onSuccess: () => {
        return `Search "${name}" created successfully`
      },
    })

    setState(EMPTY_STATE)
  }

  const updateFilter = () => {
    update("filters", `/api/${account}/filters/${id}`, {
      filter,
      name,
      count: resultsCount,
      nbFilters: filtersCount,
      modelId,
    }, {
      updateCollection: `/api/${account}/filters/${modelId}`,
      onSuccess: `Search "${name}" saved succesfully.`,
    })
  }

  const deleteFilter = (id, name) => {
    del("filters", `/api/${account}/filters/${id}`, id, {
      updateCollection: `/api/${account}/filters/${modelId}`,
      onSuccess: `Search "${name}" deleted succesfully.`,
    })
  }

  const openSaveFilterDialog = () => {
    openDialog({
      title: "Save Search",
      content: <NewName save={saveFilter} count={filtersCount} />,
    })
  }

  const updateName = name => {
    update("filters", `/api/${account}/filters/${id}/name`, {
      name,
    }, {
      updateCollection: `/api/${account}/filters/${modelId}`,
    })
  }

  const openEditFilterNameDialog = () => {
    openDialog({
      title: "Edit Saved Search Name",
      content: <EditName save={updateName} name={name} />,
    })
  }

  const openMenu = event => {
    const menuParent = event.currentTarget

    setState(state => {
      if (state.menuParent) {
        return EMPTY_STATE
      } else {
        return { menuParent }
      }
    })
  }

  const closeMenu = event => {
    // ignore events on the button
    if (event.target.id !== "filters-history" && event.target.id !== "filters-history-icon") {
      setState(EMPTY_STATE)
    }
  }

  const selectElementDefault = (filter, isSaveable = true) => {
    setFilter(filter.filter)

    if (isSaveable) {
      setCurrentFilter(filter.id, filter.name, filter.filter)
    } else {
      setCurrentFilter()
    }

    setState(EMPTY_STATE)
  }

  let selectElement = selectElementDefault
  if (modifyFilter) {
    selectElement = element => {
      // If the filter doesnt need to be modified, it returns undefined
      const filter = modifyFilter(element)
      selectElementDefault({
        ...element,
        ...(filter && { filter }),
      }, isEmpty(filter))
    }
  }

  const elements = map(filters, filter => {
    return {
      id: filter.id,
      name: filter.name,
      filter: filter.filter,
      header: <><span className="saved-search-name">{filter.name}</span><span className="filters-count">{filter.nbFilters} {pluralize("filter", filter.nbFilters)} selected</span></>,
      subheader: `${filter.count} ${pluralize("name", filter.count)} found as of ${formatNiceShortDate(filter.lastUpdated)}`,
      allowDelete: true,
    }
  })

  return (
    <div className="modules_filter-saver">
      {isFinite(resultsCount) && name && <OPTooltip title={`Edit saved search: ${name}`}><IconButton id="edit-saved-search" onClick={openEditFilterNameDialog} size="large"><Edit /></IconButton></OPTooltip>}
      {isFinite(resultsCount) && filter && <OPTooltip title="Save as..."><IconButton id="add-new-search" onClick={openSaveFilterDialog} size="large"><Add /></IconButton></OPTooltip>}
      {filter && currentFilter && !isEqual(filter, currentFilter) && <OPTooltip title="Save"><IconButton id="save-search" onClick={updateFilter} size="large"><Save /></IconButton></OPTooltip>}
      <OPTooltip title="View saved searches"><IconButton onClick={openMenu} id="filters-history" size="large"><History id="filters-history-icon" /></IconButton></OPTooltip>
      <ClickAwayListener onClickAway={closeMenu}>
        <Popper
          className="filters-menu"
          disablePortal
          anchorEl={get(state, "menuParent")}
          open={!!get(state, "menuParent")}
          placement="bottom-end"
        >
          <SearchList
            elements={elements}
            isLoading={status === LOADING}
            title="Saved Searches"
            selectElement={selectElement}
            deleteElement={deleteFilter}
            noElementDisplay={{
              primary: "No search saved yet.",
              secondary: filtersCount ? "You can save your current search to add it to this list." : "Please add a filter below to start.",
            }}
          />
        </Popper>
      </ClickAwayListener>
    </div>
  )
}

FilterSaver.propTypes = {
  account: PropTypes.string.isRequired,
  filter: PropTypes.object,
  currentFilter: PropTypes.object,
  filters: PropTypes.array.isRequired,
  filtersCount: PropTypes.number,
  resultsCount: PropTypes.number,
  name: PropTypes.string,
  id: PropTypes.string,
  del: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
  setCurrentFilter: PropTypes.func.isRequired,
  modelId: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  modifyFilter: PropTypes.func,
}

export default FilterSaver
